import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

import { toast } from 'react-hot-toast';
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Pause,
  Play,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Search,
  Pencil,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { io } from 'socket.io-client';
import AnimatedNumber from './AnimatedNumber';

// Định nghĩa các khung giờ
const TIME_RANGES = [
  { value: '1d', label: '1 ngày' },
  { value: '12h', label: '12 giờ' },
  { value: '6h', label: '6 giờ' },
  { value: '3h', label: '3 giờ' },
];

const CampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLine, setLoadingLine] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountId, setAccountId] = useState('');
  const [statusFilter, setStatusFilter] = useState('2');
  const [editingBudget, setEditingBudget] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [updatingBudget, setUpdatingBudget] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'cost',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 300,
    total: 0,
    totalPages: 0
  });
  const [campaignCharts, setCampaignCharts] = useState([]);
  const [campaignChartTotal, setCampaignChartTotal] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('3h');
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Auth helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('gads_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
    };
  };

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/accounts`, {
        headers: getAuthHeaders(),
      });
      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }
      const data = await response.json();
      const accountsList = data.accounts || [];
      setAccounts(accountsList);
      if (accountsList.length > 0) {
        setAccountId(accountsList[0].customer_id ?? 'all');
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  }, []);

  const fetchCampaigns = useCallback(async (loadingEffect = true) => {
    try {
      if (loadingEffect) {
        setLoading(true);
      } else {
        setLoadingLine(true);
      }

      let url = `${process.env.REACT_APP_API_URL}/api/campaigns?`;
      url += `accountId=${accountId}&`;
      url += `page=${pagination.page}&`;
      url += `limit=${pagination.limit}`;

      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await fetch(url, { headers: getAuthHeaders() });

      if (!response.ok) {
        throw new Error('Failed to fetch campaigns');
      }
      const data = await response.json();
      setCampaigns(data.campaigns);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      }));
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      toast.error('Không thể tải danh sách chiến dịch');
    } finally {
      setLoading(false);
      setProgressValue(100);
      setTimeout(() => {
        setLoadingLine(false);
      }, 1000);
    }
  }, [accountId, statusFilter, pagination.page, pagination.limit, searchTerm, startDate, endDate]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const sortedCampaigns = [...campaigns]

  const filteredCampaigns = sortedCampaigns.filter(campaign => {
    // Áp dụng filter theo accountId
    if (accountId && campaign.account_id != accountId) {
      return false;
    }

    // Áp dụng filter theo status
    if (statusFilter && campaign.status != statusFilter) {
      return false;
    }

    // Áp dụng filter theo search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return campaign.name.toLowerCase().includes(searchLower) ||
        campaign.id.toString().includes(searchLower);
    }

    return true;
  }).sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Xử lý các trường đặc biệt
    if (sortConfig.key == 'budget') {
      aValue = a.budget?.amount || 0;
      bValue = b.budget?.amount || 0;
    } else if (sortConfig.key == 'average_cpc') {
      aValue = a.average_cpc || 0;
      bValue = b.average_cpc || 0;
    } else if (sortConfig.key == 'cost') {
      aValue = a.cost || 0;
      bValue = b.cost || 0;
    } else if (sortConfig.key == 'conversions') {
      aValue = a.conversions || 0;
      bValue = b.conversions || 0;
    } else if (sortConfig.key == 'cost_per_conv') {
      aValue = a.conversions > 0 ? a.cost / a.conversions : 0;
      bValue = b.conversions > 0 ? b.cost / b.conversions : 0;
    }

    if (aValue < bValue) return sortConfig.direction == 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction == 'asc' ? 1 : -1;
    return 0;
  });

  // Format số tiền sang định dạng VND
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return number.toLocaleString('vi-VN').replace(/\./g, ',');
  };

  const formatPercentage = (number) => {
    return `${number.toFixed(2)}%`;
  };

  const Status = {
    UNSPECIFIED: 0,// UNSPECIFIED
    UNKNOWN: 1,// UNKNOWN
    ELIGIBLE: 2,// ELIGIBLE
    PAUSED: 3,// PAUSED
    REMOVED: 4,// REMOVED
    PENDING: 5,// PENDING
    LIMITED: 6,// LIMITED
    NOT_ELIGIBLE: 7
  }

  // Xác định màu cho badge status
  const getStatusBadgeColor = (status) => {
    switch (parseInt(status)) {
      case Status.ELIGIBLE:
        return 'bg-green-100 text-green-800';
      case Status.PAUSED:
        return 'bg-yellow-100 text-yellow-800';
      case Status.REMOVED:
        return 'bg-red-100 text-red-800';
      case Status.PENDING:
        return 'bg-blue-100 text-blue-800';
      case Status.LIMITED:
        return 'bg-orange-100 text-orange-800';
      case Status.NOT_ELIGIBLE:
        return 'bg-gray-100 text-gray-800';
      case Status.UNSPECIFIED:
        return 'bg-gray-100 text-gray-800';
      case Status.UNKNOWN:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Chuyển đổi status code sang text
  const getStatusText = (status) => {
    switch (parseInt(status)) {
      case Status.ELIGIBLE:
        return 'Đang chạy';
      case Status.PAUSED:
        return 'Tạm dừng';
      case Status.REMOVED:
        return 'Đã xóa';
      case Status.PENDING:
        return 'Đang chờ';
      case Status.LIMITED:
        return 'Giới hạn';
      case Status.NOT_ELIGIBLE:
        return 'Không hợp lệ';
      case Status.UNSPECIFIED:
        return 'Không xác định';
      case Status.UNKNOWN:
        return 'Không xác định';
      default:
        return 'Không xác định';
    }
  };

  // Xử lý các action
  const handlePauseResume = async (campaignId, currentStatus) => {
    try {
      const newStatus = currentStatus == 2 ? 3 : 2; // 2: ELIGIBLE, 3: PAUSED
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/campaigns/${campaignId}/status`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update campaign status');
      }

      // Cập nhật UI
      setCampaigns(prevCampaigns =>
        prevCampaigns.map(campaign =>
          campaign.id == campaignId
            ? { ...campaign, status: newStatus }
            : campaign
        )
      );

      // Hiển thị thông báo thành công
      toast.success(`Đã ${newStatus == 2 ? 'tiếp tục' : 'tạm dừng'} chiến dịch thành công`);
    } catch (err) {
      console.error('Error updating campaign status:', err);
      toast.error('Không thể cập nhật trạng thái chiến dịch');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleUpdateBudget = async (campaignId, newBudget) => {
    try {
      setUpdatingBudget(campaignId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/campaigns/${campaignId}/budget`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ budget: newBudget }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update campaign budget');
      }

      // Cập nhật UI
      setCampaigns(prevCampaigns =>
        prevCampaigns.map(campaign =>
          campaign.id == campaignId
            ? { ...campaign, budget_amount: newBudget }
            : campaign
        )
      );
      setEditingBudget(null);
      toast.success('Cập nhật budget thành công');
    } catch (err) {
      console.error('Error updating campaign budget:', err);
      toast.error('Không thể cập nhật budget');
    } finally {
      setUpdatingBudget(null);
    }
  };

  const handleKeyPress = (e, campaignId) => {
    if (e.key == 'Enter') {
      handleUpdateBudget(campaignId, parseFloat(editingValue));
    } else if (e.key == 'Escape') {
      setEditingBudget(null);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key == key && prev.direction == 'asc' ? 'desc' : 'asc'
    }));
  };

  // Hàm để lấy dữ liệu biểu đồ từ API
  const fetchCampaignChartData = useCallback(async (campaignIds, timeRange) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/campaigns/metrics?` +
        `campaignIds=${campaignIds.join(',')}&` +
        `timeRange=${timeRange}`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch campaign metrics');
      }

      const data = await response.json();
      return {metrics: data.metrics, metricTotal: data.metricTotal};
    } catch (error) {
      console.error('Error fetching batch chart data:', error);
      return null;
    }
  }, []);

  // Hàm để tạo dữ liệu mẫu khi không có dữ liệu thực
  const generateSampleData = useCallback((campaign, timeRange) => {
    // Tạo dữ liệu mẫu dựa trên khung giờ đã chọn
    const now = new Date();
    let intervalMinutes;
    let dataPoints;

    switch (timeRange) {
      case '1d':
        intervalMinutes = 120; // 2 giờ
        dataPoints = 12;
        break;
      case '12h':
        intervalMinutes = 60; // 1 giờ
        dataPoints = 12;
        break;
      case '6h':
        intervalMinutes = 30; // 30 phút
        dataPoints = 12;
        break;
      case '3h':
        intervalMinutes = 15; // 15 phút
        dataPoints = 12;
        break;
      default:
        intervalMinutes = 15;
        dataPoints = 12;
    }

    const dates = Array.from({ length: dataPoints }, (_, i) => {
      const date = new Date(now);
      date.setMinutes(date.getMinutes() - (intervalMinutes * (dataPoints - 1 - i)));
      return date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    });

    // Tạo dữ liệu ngẫu nhiên
    const generateRandomData = (baseValue, variance) => {
      return dates.map(() => Math.max(0, baseValue + (Math.random() * variance * 2 - variance)));
    };

    const costData = generateRandomData(0, 0);
    const conversionsData = generateRandomData(0, 0);

    // Tính CPA (Cost Per Acquisition) cho mỗi điểm dữ liệu
    const cpaData = dates.map((_, index) => {
      return 0;
    });
    return {
      labels: dates,
      cost: costData,
      conversions: conversionsData,
      cpa: cpaData
    };
  }, []);

  // Hàm để chuẩn bị dữ liệu cho biểu đồ của từng chiến dịch
  const prepareCampaignCharts = useCallback(async () => {
    // Lọc các chiến dịch đang hoạt động
    const activeCampaigns = filteredCampaigns;

    if (activeCampaigns.length == 0) {
      setCampaignCharts([]);
      setCampaignChartTotal([]);
      return;
    }

    setLoadingCharts(true);

    try {
      // Lấy dữ liệu biểu đồ cho tất cả chiến dịch trong một lần gọi API
      const campaignIds = activeCampaigns.map(campaign => campaign.id);
      const {metrics: metricsData, metricTotal} = await fetchCampaignChartData(campaignIds, selectedTimeRange);

      if (!metricsData) {
        // Nếu không lấy được dữ liệu từ API, sử dụng dữ liệu mẫu cho tất cả chiến dịch
        const charts = activeCampaigns.map(campaign => {
          const data = generateSampleData(campaign, selectedTimeRange);
          const chartData = data.labels.map((label, index) => ({
            name: label,
            cost: data.cost[index],
            conversions: data.conversions[index],
            cpa: data.cpa[index]
          }));

          return {
            id: campaign.id,
            name: campaign.name,
            isSampleData: false,
            data: chartData
          };
        });

        setCampaignCharts(charts);
        setCampaignChartTotal(charts.data[0]);
        return;
      }

      // Xử lý dữ liệu metrics từ API
      const charts = activeCampaigns.map(campaign => {
        const campaignMetrics = metricsData.find(m => m.campaignId == campaign.id);

        if (!campaignMetrics) {
          // Nếu không có dữ liệu cho chiến dịch này, sử dụng dữ liệu mẫu
          const data = generateSampleData(campaign, selectedTimeRange);
          const chartData = data.labels.map((label, index) => ({
            name: label,
            cost: data.cost[index],
            conversions: data.conversions[index],
            cpa: data.cpa[index]
          }));

          return {
            id: campaign.id,
            name: campaign.name,
            isSampleData: false,
            data: chartData
          };
        }

        // Chuyển đổi dữ liệu sang định dạng phù hợp với Recharts
        const chartData = campaignMetrics.labels.map((label, index) => ({
          name: label,
          cost: campaignMetrics.cost[index],
          conversions: campaignMetrics.conversions[index],
          cpa: campaignMetrics.cpa[index]
        }));

        return {
          id: campaign.id,
          name: campaign.name,
          isSampleData: false,
          data: chartData
        };
      });

      setCampaignCharts(charts);

      const chartTotalData = metricTotal.labels.map((label, index) => ({
        name: label,
        cost: metricTotal.cost[index],
        conversions: metricTotal.conversions[index],
        cpa: metricTotal.cpa[index]
      }));
      setCampaignChartTotal(chartTotalData);
    } catch (error) {
      console.error('Error preparing campaign charts:', error);
      toast.error('Không thể tải dữ liệu biểu đồ');
    } finally {
      setLoadingCharts(false);
    }
  }, [campaigns, selectedTimeRange, fetchCampaignChartData, generateSampleData]);

  // Cập nhật biểu đồ khi danh sách chiến dịch hoặc khung giờ thay đổi
  useEffect(() => {
    if (campaigns.length > 0) {
      prepareCampaignCharts();
    }
  }, [campaigns, prepareCampaignCharts]);

  // Hàm tính tổng các thông số
  const calculateTotals = useCallback((campaigns) => {
    return campaigns.reduce((acc, campaign) => {
      acc.cost += campaign.cost || 0;
      acc.conversions += campaign.conversions || 0;
      acc.impressions += campaign.impressions || 0;
      acc.clicks += campaign.clicks || 0;
      acc.views += campaign.views || 0;
      return acc;
    }, {
      cost: 0,
      conversions: 0,
      impressions: 0,
      clicks: 0,
      views: 0
    });
  }, []);

  // Hàm cập nhật danh sách campaign từ socket
  const updateCampaignsFromSocket = useCallback((newCampaigns) => {
    setCampaigns(prevCampaigns => {
      // Tạo map của campaigns hiện tại để dễ dàng cập nhật
      const campaignMap = new Map(prevCampaigns.map(campaign => [campaign.id, campaign]));

      // Cập nhật hoặc thêm mới campaigns từ socket
      newCampaigns.forEach(campaign => {
        campaignMap.set(campaign.id, campaign);
      });

      // Chuyển map thành array và áp dụng filter và sort
      const updatedCampaigns = Array.from(campaignMap.values())
        .sort((a, b) => {
          if (!sortConfig.key) return 0;

          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];

          // Xử lý các trường đặc biệt
          if (sortConfig.key == 'budget') {
            aValue = a.budget?.amount || 0;
            bValue = b.budget?.amount || 0;
          } else if (sortConfig.key == 'average_cpc') {
            aValue = a.average_cpc || 0;
            bValue = b.average_cpc || 0;
          } else if (sortConfig.key == 'cost') {
            aValue = a.cost || 0;
            bValue = b.cost || 0;
          } else if (sortConfig.key == 'conversions') {
            aValue = a.conversions || 0;
            bValue = b.conversions || 0;
          } else if (sortConfig.key == 'cost_per_conv') {
            aValue = a.conversions > 0 ? a.cost / a.conversions : 0;
            bValue = b.conversions > 0 ? b.cost / b.conversions : 0;
          }

          if (aValue < bValue) return sortConfig.direction == 'asc' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction == 'asc' ? 1 : -1;
          return 0;
        });

      return updatedCampaigns;
    });
  }, [accountId, statusFilter, searchTerm, sortConfig]);


  useEffect(() => {
    fetchAccounts();
  }, [])

  useEffect(() => {
    fetchCampaigns(false);
  }, [accountId, statusFilter, pagination.page, pagination.limit, searchTerm, startDate, endDate])

  useEffect(() => {
    // Kết nối Socket.IO
    const socket = io(`${process.env.REACT_APP_API_URL}/campaigns`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5
    });

    // Chỉ lắng nghe socket khi không có filter khoảng thời gian
    const shouldListenSocket = !startDate || !endDate;

    if (shouldListenSocket) {
      // Lắng nghe sự kiện cập nhật campaign
      socket.on('campaigns_updated', ({ customerId, campaigns }) => {
        // Nếu đang filter theo accountId và accountId trùng với customerId được cập nhật
        if (!accountId || accountId == customerId) {
          // Sử dụng dữ liệu từ socket thay vì gọi lại API
          updateCampaignsFromSocket(campaigns);
        }
      });

      // Lắng nghe sự kiện cập nhật conversion
      socket.on('conversion_updated', ({ customerId, campaignName, conversions }) => {
        // Nếu đang filter theo accountId và accountId trùng với customerId được cập nhật
        if (!accountId || accountId == customerId) {
          setCampaigns(prevCampaigns =>
            prevCampaigns.map(campaign =>
              campaign.name == campaignName
                ? { ...campaign, conversions }
                : campaign
            )
          );
        }
      });
    }

    return () => {
      socket.disconnect();
    };
  }, [fetchCampaigns, accountId, statusFilter, pagination.page, pagination.limit, searchTerm, startDate, endDate, updateCampaignsFromSocket]);

  useEffect(() => {
    let interval;
    if (loadingLine) {
      setProgressValue(0);
      interval = setInterval(() => {
        setProgressValue((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 25;
        });
      }, 100);
    } else {
      setProgressValue(0);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loadingLine]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-md font-bold">Danh sách chiến dịch</h2>
      </div>

      <div className="relative">
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
          <Button
            onClick={() => fetchCampaigns(false)}
            className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg"
          >
            Làm mới
          </Button>
        </div>
      </div>


      {/* Search, Time Range and Per Page Select - Always visible */}
      <div className="flex items-center justify-between mb-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 w-full">
          <div className="relative w-full md:w-auto">
            <label className="text-[12px] text-gray-500">Tìm kiếm</label>

            <Search className="absolute left-2 top-[35px] h-4 w-4 text-gray-500" />
            <Input
              placeholder="Tìm kiếm theo tên hoặc ID..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-8 w-full md:w-[300px] bg-white"
            />
          </div>
          <div className="relative w-full md:w-auto">
            <label className="text-[12px] text-gray-500">Account</label>
            <Select
              value={accountId || 'all'}
              onValueChange={(value) => {
                setAccountId(value == 'all' ? '' : value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="bg-white text-[12px] text-gray-500 w-full md:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.customer_id} value={account.customer_id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative w-full md:w-auto">
            <label className="text-[12px] text-gray-500">Trạng thái</label>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(value) => {
                setStatusFilter(value == 'all' ? '' : value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="bg-white text-[12px] text-gray-500 w-full md:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                <SelectItem value="2">Đang chạy</SelectItem>
                <SelectItem value="3">Tạm dừng</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-2 w-full md:w-auto">
            <div className="relative w-full md:w-auto">
              <label className="text-[12px] text-gray-500">Từ ngày</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setStatusFilter('');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="bg-white text-[12px] text-gray-500 w-full md:w-auto h-9 px-3 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 hover:border-gray-300"
              />
            </div>
            <div className="relative w-full md:w-auto">
              <label className="text-[12px] text-gray-500">Đến ngày</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setStatusFilter('');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="bg-white text-[12px] text-gray-500 w-full md:w-auto h-9 px-3 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 hover:border-gray-300"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 md:mt-0">
          <div className="relative hidden md:block">
            <label className="text-[12px] text-gray-500">Hiển thị</label>
            <Select
              value={pagination.limit.toString()}
              onValueChange={(value) => {
                setPagination(prev => ({
                  ...prev,
                  limit: parseInt(value),
                  page: 1
                }));
              }}
            >
              <SelectTrigger className="w-[100px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
                <SelectItem value="300">300</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto bg-white">
        <div className="w-full">
          <Progress value={progressValue} className="h-1 bg-gray-100" />
        </div>
        <div className="min-w-[1200px]">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow className="text-[12px]">
                <TableHead className="w-[120px] text-[11px]">Account</TableHead>
                <TableHead className="w-[100px] text-[11px]">Campaign Name</TableHead>
                <TableHead className="w-[120px] text-right text-[11px]">Budget</TableHead>
                <TableHead className="w-[100px] text-center text-[11px]">Actions</TableHead>
                <TableHead className="w-[100px] text-center text-[11px]">Status</TableHead>

                <TableHead className="w-[100px] text-right text-[11px] cursor-pointer" onClick={() => handleSort('cost')}>
                  Cost
                  {sortConfig.key == 'cost' && (
                    <span>{sortConfig.direction == 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableHead>
                <TableHead className="w-[100px] text-right text-[11px] cursor-pointer" onClick={() => handleSort('conversions')}>
                  Conv
                  {sortConfig.key == 'conversions' && (
                    <span>{sortConfig.direction == 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableHead>
                <TableHead className="w-[100px] text-right text-[11px] cursor-pointer" onClick={() => handleSort('cost_per_conv')}>
                  Cost/Conv
                  {sortConfig.key == 'cost_per_conv' && (
                    <span>{sortConfig.direction == 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableHead>

                <TableHead className="w-[100px] text-right text-[11px]">Impr.</TableHead>
                <TableHead className="w-[100px] text-right text-[11px]">Clicks</TableHead>
                <TableHead className="w-[100px] text-right text-[11px]">Cost.A.Conv</TableHead>
                <TableHead className="w-[100px] text-right text-[11px] cursor-pointer" onClick={() => handleSort('average_cpc')}>
                  Avg. CPC
                  {sortConfig.key == 'average_cpc' && (
                    <span>{sortConfig.direction == 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableHead>
                <TableHead className="w-[100px] text-right text-[11px]">CTR</TableHead>
                <TableHead className="w-[100px] text-right text-[11px]">Avg. CPM</TableHead>
                <TableHead className="w-[100px] text-right text-[11px]">Avg. CPV</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                // Skeleton loading state
                Array.from({ length: pagination.limit }).map((_, index) => (
                  <TableRow key={index} className="text-[12px]">
                    <TableCell className="w-[120px] text-[11px]"><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell className="w-[100px] text-[11px]"><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell className="w-[150px] text-[11px]"><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell className="w-[100px] text-[11px]"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="w-[100px] text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-right text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-right text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-right text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-right text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[100px] text-right text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="w-[200px] text-right sticky right-0 bg-white z-10 text-[11px]"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : campaigns.length == 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-8">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <AlertCircle className="h-8 w-8 mb-2" />
                      <p>Không có kết quả nào</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.id} className="text-[12px]">
                      <TableCell className="w-[120px] bg-white text-[12px]">
                        {accounts.find(account => account.customer_id == campaign.account_id)?.name || 'N/A'}
                      </TableCell>
                      <TableCell className="w-[100px] bg-white text-[12px]">{campaign.name}</TableCell>
                      <TableCell className="w-[100px] bg-white text-right text-[12px]">
                        {editingBudget == campaign.id ? (
                          <div className="w-[100px] flex items-center justify-end gap-2">
                            <Input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => handleKeyPress(e, campaign.id)}
                              className="w-[100px] h-6 text-right text-[11px]"
                              autoFocus
                              disabled={updatingBudget == campaign.id}
                            />
                            {updatingBudget == campaign.id && (
                              <div className="animate-spin">
                                <svg className="h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-[100px] flex items-center justify-end gap-2">
                            {formatCurrency(campaign.budget_amount || 0)}
                            {updatingBudget == campaign.id ? (
                              <div className="animate-spin">
                                <svg className="h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            ) : (
                              <Pencil
                                className="h-3 w-3 cursor-pointer text-gray-500 hover:text-gray-700"
                                onClick={() => {
                                  setEditingBudget(campaign.id);
                                  setEditingValue(campaign.budget_amount || 0);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="w-[100px] bg-white text-center text-[12px]">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePauseResume(campaign.id, campaign.status)}
                            title={campaign.status == 2 ? 'Tạm dừng' : 'Tiếp tục'}
                            className="h-6 w-6 bg-gray-200"
                          >
                            {campaign.status == 2 ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="w-[110px] flex bg-white justify-center">
                        <Button variant="ghost" className={getStatusBadgeColor(campaign.status) + ' text-[12px] px-2 py-1 h-6 text-[10px]'}>
                          {getStatusText(campaign.status)}
                        </Button>
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.cost || 0}
                          format={formatCurrency}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.conversions || 0}
                          format={(v) => v}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.conversions > 0 ? campaign.cost / campaign.conversions : 0}
                          format={formatCurrency}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.impressions || 0}
                          format={formatNumber}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.clicks || 0}
                          format={formatNumber}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.cost - campaign.last_time_cost || 0}
                          format={formatCurrency}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.average_cpc || 0}
                          format={formatCurrency}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.ctr || 0}
                          format={formatPercentage}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.impressions > 0 ? (campaign.cost / campaign.impressions) * 1000 : 0}
                          format={formatCurrency}
                        />
                      </TableCell>
                      <TableCell className="w-[100px] text-right bg-white text-[12px]">
                        <AnimatedNumber
                          value={campaign.views > 0 ? campaign.cost / campaign.views : 0}
                          format={formatCurrency}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Footer rows */}
                  <TableRow className="text-[12px] font-bold bg-gray-50">
                    <TableCell colSpan={5}>Tổng cộng (Filtered):</TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).cost}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).conversions}
                        format={(v) => v}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).conversions > 0
                          ? calculateTotals(filteredCampaigns).cost / calculateTotals(filteredCampaigns).conversions
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).impressions}
                        format={formatNumber}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).clicks}
                        format={formatNumber}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).views}
                        format={formatNumber}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).clicks > 0
                          ? calculateTotals(filteredCampaigns).cost / calculateTotals(filteredCampaigns).clicks
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).impressions > 0
                          ? (calculateTotals(filteredCampaigns).clicks / calculateTotals(filteredCampaigns).impressions) * 100
                          : 0}
                        format={formatPercentage}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).impressions > 0
                          ? (calculateTotals(filteredCampaigns).cost / calculateTotals(filteredCampaigns).impressions) * 1000
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(filteredCampaigns).views > 0
                          ? calculateTotals(filteredCampaigns).cost / calculateTotals(filteredCampaigns).views
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className="text-[12px] font-bold bg-blue-50">
                    <TableCell colSpan={5}>Tổng cộng (Account):</TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).cost}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).conversions}
                        format={(v) => v}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).conversions > 0
                          ? calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).cost / calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).conversions
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).impressions}
                        format={formatNumber}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).clicks}
                        format={formatNumber}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).views}
                        format={formatNumber}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).clicks > 0
                          ? calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).cost / calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).clicks
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).impressions > 0
                          ? (calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).clicks / calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).impressions) * 100
                          : 0}
                        format={formatPercentage}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).impressions > 0
                          ? (calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).cost / calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).impressions) * 1000
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <AnimatedNumber
                        value={calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).views > 0
                          ? calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).cost / calculateTotals(campaigns.filter(c => c.account_id == accountId || accountId != 'all')).views
                          : 0}
                        format={formatCurrency}
                      />
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination - Always visible */}
      <div className="flex flex-col md:flex-row items-center justify-between mt-4 bg-white p-4 rounded-lg shadow-sm gap-4">
        <div className="text-sm text-gray-500">
          Hiển thị {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} trên {pagination.total} kết quả
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page == 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm">
            Trang {pagination.page} / {pagination.totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page == pagination.totalPages || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Hiển thị biểu đồ cho từng chiến dịch đang chạy */}
      {campaignCharts.length > 0 && (
        <>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium">Biểu đồ chiến dịch đang chạy</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Khung giờ:</span>
                <Select
                  value={selectedTimeRange}
                  onValueChange={setSelectedTimeRange}
                >
                  <SelectTrigger className="w-[100px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={prepareCampaignCharts}
                  className="h-8 text-xs"
                  disabled={loadingCharts}
                >
                  {loadingCharts ? 'Đang tải...' : 'Cập nhật'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4 mb-5">
              {CampaignChart({id: 'total', name: 'Total Campaign', data: campaignChartTotal})}
            </div>
            {(
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {campaignCharts.map((chart) => (
                  CampaignChart(chart)
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const CampaignChart = (chart) => {
  const generateTicks = (data, key, step = 50, maxTicks = 10, baseTicks = [0, 1, 2, 3, 4, 5]) => {
    const maxValue = Math.max(...data.map((item) => item[key]));
    const lastTick = Math.max(0, ...baseTicks);
    for (let i = lastTick; i <= maxValue && baseTicks.length < maxTicks; i += step) {
      baseTicks.push(i);
    }
    if (baseTicks[baseTicks.length - 1] < maxValue) {
      baseTicks.push(Math.ceil(maxValue / step) * step);
    }
    return baseTicks;
  };

  const costTicks = generateTicks(chart.data, 'cost', 25000, 10, [25000, 50000, 75000, 100000]);
  const convTicks = generateTicks(chart.data, 'conversions', 1, 10, [1, 2, 3, 4, 5]);

  return (
    <div key={chart.id} className="border rounded-lg p-3">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-xs font-medium truncate" title={chart.name}>
          {chart.name}
        </h4>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chart.data}
            margin={{
              top: 5,
              right: 5,
              bottom: 5,
              left: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 9 }}
              height={20}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 9 }}
              width={40}
              tickFormatter={(value) => {
                return (value / 1000) + 'k';
              }}
              domain={[0, costTicks[costTicks.length - 1]]}
              ticks={costTicks}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 9 }}
              width={40}
              domain={[0, convTicks[convTicks.length - 1]]}
              ticks={convTicks}
            />
            <Tooltip
              contentStyle={{
                fontSize: '10px',
                padding: '5px'
              }}
            />
            <Legend
              wrapperStyle={{
                fontSize: '10px',
                paddingTop: '5px'
              }}
            />
            <Bar
              yAxisId="right"
              dataKey="conversions"
              name="Lượt chuyển đổi"
              fill="#ffc658"
              barSize={10}
            />
            <Line
              yAxisId="left"
              dataKey="cost"
              name="Chi phí (VND)"
              stroke="#8884d8"
              strokeWidth={2}
              dot={{ r: 0 }}
              activeDot={{ r: 1 }}
            />
            <Line
              yAxisId="left"
              dataKey="cpa"
              name="CPA (VND)"
              stroke="#82ca9d"
              strokeWidth={2}
              dot={{ r: 0 }}
              activeDot={{ r: 1 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default CampaignList;
